import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type {
  WorkoutSetRecord,
  WorkoutExerciseDimensionRecord,
  WorkoutSetValueRecord,
} from '~/powersync/generated/schema';

const formatNumber = (value: number): string =>
  Number.isInteger(value)
    ? value.toString()
    : (Math.round(value * 100) / 100).toString();

/**
 * Builds the localized exercise set-summary subtitle shown on chat workout
 * cards, e.g. "3 série · 12 opak · 15-20 kg". Reps render with the abbreviated
 * "opak"/"reps" label; other dimensions use their unit short name. Each numeric
 * dimension collapses to a single value when all sets share it, otherwise a
 * min-max range.
 */
export const useExerciseSetSummary = (
  exerciseId: string,
  workoutId: string,
): string => {
  const { t } = useTranslation('workout');

  const { data: sets } = useLivePowerSyncQuery<WorkoutSetRecord>(
    db.Workout.queries.findSetsByExercise(exerciseId),
    [exerciseId],
  );

  const { data: dimensions } =
    useLivePowerSyncQuery<WorkoutExerciseDimensionRecord>(
      db.Workout.queries.findDimensionsByExercise(exerciseId),
      [exerciseId],
    );

  const { data: setValues } = useLivePowerSyncQuery<WorkoutSetValueRecord>(
    workoutId ? db.Workout.queries.findSetValuesByWorkout(workoutId) : null,
    [workoutId],
  );

  return useMemo(() => {
    const setList = sets ?? [];
    if (setList.length === 0) {
      return '';
    }

    const setIds = new Set(setList.map((s) => s.id));
    const valuesByDimension = new Map<string, number[]>();
    for (const value of setValues ?? []) {
      const dimensionId = value.exerciseDimensionId;
      if (!value.setId || !setIds.has(value.setId) || !dimensionId) continue;
      const list = valuesByDimension.get(dimensionId) ?? [];
      list.push(Number(value.value));
      valuesByDimension.set(dimensionId, list);
    }

    const parts: string[] = [
      t('exerciseSummary.sets', { count: setList.length }),
    ];

    for (const dimension of dimensions ?? []) {
      const values = valuesByDimension.get(dimension.id) ?? [];
      if (values.length === 0) continue;

      const min = Math.min(...values);
      const max = Math.max(...values);
      const numeric =
        min === max
          ? formatNumber(min)
          : `${formatNumber(min)}-${formatNumber(max)}`;

      const label =
        dimension.dimension === 'REPS'
          ? t('exerciseSummary.reps')
          : dimension.unit && dimension.unit.toUpperCase() !== 'NONE'
            ? t(`unit.${dimension.unit}.shortName`)
            : '';

      parts.push(label ? `${numeric} ${label}` : numeric);
    }

    return parts.join(' · ');
  }, [sets, dimensions, setValues, t]);
};
